<script lang="ts" setup>
import { computed } from 'vue'
import './_index.scss'

import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'
import RoomieTag from '@lahaus-roomie/roomie/src/components/RoomieTag/index.vue'

import IconImage from '@lahaus-roomie/roomie/src/assets/icons/image.svg'
import IconVideo from '@lahaus-roomie/roomie/src/assets/icons/video.svg'
import IconMapPin from '@lahaus-roomie/roomie/src/assets/icons/map-pin.svg'

import type { Props } from './types'
import AppCarrousel from '@/components/App/Carrousel/index.vue'
import PdFinancialInfo from '@/components/pd/FinancialInfo/index.vue'
import PdFeaturesSummary from '@/components/pd/FeaturesSummary/index.vue'

import { getDeliveryTypeLabel } from '@/utils/project'

const { t } = useI18n()
const { buildImageUrl } = useImsImageProvider()

const props = defineProps<Props>()

const emit = defineEmits(['openGallery'])

const getImages = (imageType: string) => {
  return props.listing.pictures_v2?.filter(image => image.type === imageType).sort((a, b) => a.order - b.order).map(image => image.url)
}

const carrouselImages = computed(() => {
  if (!props.listing.pictures_v2) return []

  return [
    props.listing.cover,
    ...getImages('gallery')
  ]
})

const itemsPerSlideDesktop = Math.min(carrouselImages.value.length, 3)

const carrouselImageSources = carrouselImages.value.map((url: string) => {
  return [
    {
      mediaWidth: 767,
      src: buildImageUrl(url, { width: 1000 })
    },
    {
      mediaWidth: 1023,
      src: buildImageUrl(url, { width: 1200 })
    },
    {
      mediaWidth: 2500,
      src: buildImageUrl(url, { width: 2000 })
    }
  ]
})

const ctas = computed(() => (
  [
    {
      id: 'image',
      icon: IconImage,
      label: t('ctas.image', { n: props.listing.pictures_v2?.length }),
      action: () => emit('openGallery', 'pictures'),
      renderCondition: props.listing.pictures_v2?.length > 0
    },
    {
      id: 'video',
      icon: IconVideo,
      label: t('ctas.video'),
      action: () => emit('openGallery', 'videos'),
      renderCondition: props.listing.raw_listing?.attributes?.videos?.length > 0
    },
    {
      id: 'location',
      icon: IconMapPin,
      label: t('ctas.location'),
      action: () => emit('openGallery', 'location'),
      renderCondition: !!props.listing.raw_listing?.location?.latitude && !!props.listing.raw_listing?.location?.longitude
    }
  ])
)

</script>

<script lang="ts">
export default {
  name: 'PdHero'
}
</script>

<template>
  <section class="pd-hero">
    <div
      class="pd-hero__gallery"
      data-lh-id="pd-hero-gallery-open">
      <AppCarrousel
        id="pd-hero"
        class="pd-hero__gallery-carrousel hidden lg:flex"
        :pictures="carrouselImages"
        :pictures-sources="carrouselImageSources"
        picture-container-styles="flex gap-4"
        anchor-styles="w-full h-full flex"
        img-styles="object-cover w-full h-full -z-1"
        dots-styles="!bottom-12 z-40"
        :items-per-slide="itemsPerSlideDesktop"
        @image-clicked="emit('openGallery', 'pictures')" />

      <AppCarrousel
        id="pd-hero"
        class="pd-hero__gallery-carrousel flex lg:hidden"
        :pictures="carrouselImages"
        :pictures-sources="carrouselImageSources"
        anchor-styles="w-full h-full flex"
        img-styles="object-cover w-full h-full -z-1"
        dots-styles="dot-styles"
        :items-per-slide="1"
        @image-clicked="emit('openGallery', 'pictures')" />

      <div
        class="pd-hero__gallery-info-container text-white pointer-events-none"
        data-lh-id="app-carrousel-picture-link-pd-hero">
        <div class="pd-hero__gallery-info-container-top">
          <div class="page-pd-city-slug__section page-pd-city-slug__section--padded py-16 lg:py-36 flex flex-row justify-between max-w-[1600px]">
            <div class="pointer-events-none flex flex-col lg:gap-4 lg:flex-col-reverse lg:items-start">
              <div class="flex flex-col gap-4 lg:flex-row-reverse lg:gap-8 lg:items-center">
                <RoomieTag
                  v-if="listing.ims?.delivered_at"
                  id="pd-hero-delivery-tag"
                  class="h-fit w-fit"
                  variant="success"
                  :label="getDeliveryTypeLabel(listing.ims.delivered_at, t)" />

                <h1 class="text-28 lg:text-32 font-bold">
                  {{ listing.name }}
                </h1>
              </div>

              <h3 class="text-white text-14 lg:text-16">
                {{ listing.neighborhood_label }}, {{ listing.city_label }}
              </h3>
            </div>

            <PdFinancialInfo
              class="hidden lg:flex gap-64"
              :listing />
          </div>
        </div>

        <div class="pd-hero__gallery-info-container-bottom">
          <div class="page-pd-city-slug__section page-pd-city-slug__section--padded flex gap-4 md:gap-8 pointer-events-auto flex-wrap max-w-[1600px]">
            <template
              v-for="cta in ctas"
              :key="`pdp-hero-button-open-${cta.id}-tab`">
              <RoomieButton
                v-if="cta.renderCondition"
                :id="`pdp-hero-button-open-${cta.id}-tab`"
                :data-lh-id="`pdp-hero-button-open-${cta.id}-tab`"
                :aria-label="cta.label"
                variant="outlined"
                class="!bg-white"
                size="sm"
                @click="cta.action">
                <div class="flex flex-row gap-4 items-center">
                  <component
                    :is="cta.icon"
                    class="w-16 h-16 fill-primary-600" />

                  <p>
                    {{ cta.label }}
                  </p>
                </div>
              </RoomieButton>
            </template>
          </div>
        </div>
      </div>
    </div>

    <PdFeaturesSummary
      v-if="showFeaturesSummary"
      :listing />
  </section>
</template>

<i18n src="./i18n.json" lang="json"></i18n>
